/**
 * Layout configuration per diagram type.
 * Central knowledge of "what layout works best for which diagram type."
 */

import type { Direction, DiagramType, LayoutConfig } from '../types';

const DEFAULT_DIRECTIONS: Record<DiagramType, Exclude<Direction, 'auto' | 'compact' | 'spaced'>> = {
  flowchart: 'TD',
  state: 'TD',
  class: 'TD',
  er: 'TD',
  mindmap: 'TD',
  gantt: 'LR',
  timeline: 'LR',
};

const DEFAULT_CONFIGS: Record<DiagramType, Omit<LayoutConfig, 'direction'>> = {
  flowchart: { nodesep: 60, ranksep: 80, edgesep: 20, marginx: 40, marginy: 40, ranker: 'network-simplex' },
  state: { nodesep: 60, ranksep: 80, edgesep: 20, marginx: 40, marginy: 40, ranker: 'network-simplex' },
  class: { nodesep: 80, ranksep: 100, edgesep: 25, marginx: 40, marginy: 40, ranker: 'network-simplex', align: 'UL' },
  er: { nodesep: 80, ranksep: 100, edgesep: 25, marginx: 40, marginy: 40, ranker: 'network-simplex' },
  mindmap: { nodesep: 60, ranksep: 80, edgesep: 20, marginx: 40, marginy: 40, ranker: 'network-simplex' },
  gantt: { nodesep: 60, ranksep: 80, edgesep: 20, marginx: 40, marginy: 40, ranker: 'network-simplex' },
  timeline: { nodesep: 60, ranksep: 80, edgesep: 20, marginx: 40, marginy: 40, ranker: 'network-simplex' },
};

/** Compact ER tier: ~33% tighter than auto for small diagrams. */
const ER_COMPACT_CONFIG: Omit<LayoutConfig, 'direction'> = {
  nodesep: 50, ranksep: 65, edgesep: 15, marginx: 30, marginy: 30, ranker: 'network-simplex',
};

/** Spaced ER tier: more generous gaps between entities. */
const ER_SPACED_CONFIG: Omit<LayoutConfig, 'direction'> = {
  nodesep: 120, ranksep: 160, edgesep: 60, marginx: 30, marginy: 30, ranker: 'network-simplex',
};

/** Compact class tier: ~33% tighter than auto for small diagrams. */
const CLASS_COMPACT_CONFIG: Omit<LayoutConfig, 'direction'> = {
  nodesep: 50, ranksep: 65, edgesep: 15, marginx: 30, marginy: 30, ranker: 'network-simplex', align: 'UL',
};

/** Spaced class tier: more generous gaps between classes. */
const CLASS_SPACED_CONFIG: Omit<LayoutConfig, 'direction'> = {
  nodesep: 120, ranksep: 160, edgesep: 60, marginx: 30, marginy: 30, ranker: 'network-simplex', align: 'UL',
};

/** Returns the best-fit direction for a given diagram type. */
export function getDefaultDirection(diagramType: DiagramType): Exclude<Direction, 'auto' | 'compact' | 'spaced'> {
  return DEFAULT_DIRECTIONS[diagramType] ?? 'TD';
}

/** Merges diagram defaults with user direction; resolves 'auto' to diagram default. */
export function resolveLayoutConfig(diagramType: DiagramType, direction: Direction): LayoutConfig {
  // ER/Class arrangement modes: compact/spaced/auto all resolve to TD with tier-specific spacing
  if ((diagramType === 'er' || diagramType === 'class') && (direction === 'compact' || direction === 'spaced')) {
    const defaults = diagramType === 'er'
      ? (direction === 'compact' ? ER_COMPACT_CONFIG : ER_SPACED_CONFIG)
      : (direction === 'compact' ? CLASS_COMPACT_CONFIG : CLASS_SPACED_CONFIG);
    return {
      ...defaults,
      direction: 'TB',
    };
  }

  const resolvedDir: Exclude<Direction, 'auto' | 'compact' | 'spaced'> =
    direction === 'auto' || direction === 'compact' || direction === 'spaced'
      ? getDefaultDirection(diagramType)
      : direction;

  // Normalize TD alias
  const normalizedDir: Exclude<Direction, 'auto' | 'compact' | 'spaced'> = resolvedDir === 'TD' ? 'TB' : resolvedDir;

  const defaults = DEFAULT_CONFIGS[diagramType] ?? DEFAULT_CONFIGS.flowchart;

  // Increase rank spacing for horizontal layouts to compensate for wider nodes
  const ranksep = (normalizedDir === 'LR' || normalizedDir === 'RL')
    ? defaults.ranksep * 2
    : defaults.ranksep;

  return {
    ...defaults,
    direction: normalizedDir,
    ranksep,
  };
}
